import React, { useEffect, useState } from "react";
import CompanyService, { Entities } from "../../../../services/CompanyService";
import { TranslationService } from "../../../../services/TranslationService";
import Dropdown from "../../../shared/components/Dropdown";
import {
  EntityField,
  sortEntityFields
} from "../../../shared/entities/EntityField";
import { EditProps } from "../EditAction";
import { Field } from "./FieldHelpers";
import { endpointRequest } from "../../../../utils/RequestUtils";
import UserService from "../../../../services/UserService";
import { ActionType } from "../../../../services/ActionService";
import { addIf } from "../../../../utils/Utils";

export const insertAtCursor = (
  content: string,
  value: string,
  start: number,
  end: number
) => {
  const from = Math.max(0, Math.min(start, content.length));
  const to = Math.max(from, Math.min(end, content.length));
  return {
    content: content.substring(0, from) + value + content.substring(to),
    caret: from + value.length
  };
};

export const InsertFields = (props: EditProps) => {
  const generateInsertFields = (fields: string[]) =>
    fields.map((x) => ({
      value: `[${TranslationService.translate[x]}]`,
      text: TranslationService.translate[x]
    }));

  const clientFixedFieldOptions = generateInsertFields(["Client"]);
  const contactFixedFieldOptions = generateInsertFields(["Contact"]);
  const fixedInsertIOFieldOptions = generateInsertFields([
    "Amount",
    "DueAmount",
    "NotDueAmount"
  ]);
  // const fixedUserFields = generateInsertFields(["UserFirstName", "UserLastName", "UserEmail", "UserSignature"])

  const hasSegmentsWithUser = props.data.segments?.some((el) => el.UserID);

  const clientAdditionalFieldOptions = [
    ...CompanyService.getAdditionalDefinitionsFiltered(Entities.Client).map(
      (x) => new EntityField(x)
    )
  ]
    .sort(sortEntityFields(CompanyService.getPersonSortedFields().split(",")))
    .map((x) => ({ value: `[${x.title}]`, text: x.title ?? "" }));

  const fixedFields = React.useMemo(() => {
    let fields: any[] = [
      { text: TranslationService.translate.EmailInsertField, value: "" }
    ];
    switch (props.data.Type) {
      case ActionType.MailSend:
        fields = [
          ...fields,
          ...clientFixedFieldOptions,
          ...contactFixedFieldOptions,
          ...generateInsertFields(["Notes"]),
          ...clientAdditionalFieldOptions,
          ...fixedInsertIOFieldOptions,
          ...[
            {
              value: `[${TranslationService.translate.Invoices}]`,
              text: TranslationService.translate.InvoiceListR
            }
          ],
          ...addIf(CompanyService.getSetting("customerportalenabled"), {
            value: "[Portal] ",
            text: TranslationService.translate.CustomerPortal
          }),
          // // fixedUserFields,
          // ...generateInsertFields(['FollowUpMail']),
          ...addIf(Boolean(hasSegmentsWithUser), {
            value: `[${TranslationService.translate["UserSignature"]}]`,
            text: TranslationService.translate["UserSignature"]
          })
        ];
        break;
      case ActionType.ActionSMSSend:
        fields = [
          ...fields,
          ...clientFixedFieldOptions,
          ...contactFixedFieldOptions,
          ...fixedInsertIOFieldOptions,
          ...generateInsertFields(["ClaimableAmount"]),
          {
            value: "[linkportal] ",
            text: TranslationService.translate.CustomerPortal
          }
          // ...fixedUserFields
        ];
        break;
      case ActionType.ActionSendLetter:
        fields = [
          ...fields,
          ...clientFixedFieldOptions,
          ...contactFixedFieldOptions,
          ...clientAdditionalFieldOptions,
          ...fixedInsertIOFieldOptions,
          ...generateInsertFields(["Invoices", "Date"])
        ];
        break;
      default:
        fields = [];
        break;
    }
    return fields;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    props.data.Type,
    clientFixedFieldOptions,
    contactFixedFieldOptions,
    clientAdditionalFieldOptions,
    fixedInsertIOFieldOptions
  ]);

  const onChange = (value: string) => {
    if (!value?.length) {
      return;
    }
    const cursor = props.contentCursorRef?.current;
    const textarea = cursor?.touched ? cursor.textarea : null;
    if (!textarea) {
      props.setData((old) => ({
        ...old,
        Content: (old.Content ?? "") + value
      }));
      return;
    }
    const { content, caret } = insertAtCursor(
      textarea.value,
      value,
      textarea.selectionStart,
      textarea.selectionEnd
    );
    props.setData((old) => ({ ...old, Content: content }));
    // Restaurar el foco y dejar el cursor después del campo insertado
    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(caret, caret);
    }, 0);
  };

  return (
    <Field title={TranslationService.translate.AddMoreFields}>
      <Dropdown onChange={onChange} items={fixedFields} value={""} />
    </Field>
  );
};

export const InsertFieldsOnly = (props: EditProps) => (
  <div className="row">
    <InsertFields {...props} />
  </div>
);

export const InsertFieldsSignature = (props: EditProps) => {
  const [signatures, setSignatures] = useState<
    { value: number; text: string }[]
  >([]);
  useEffect(() => {
    endpointRequest(
      () => UserService.getSignatures(),
      (x) =>
        setSignatures(
          x.map((y) => ({
            value: y.SignatureID,
            text: UserService.getUserName(y.UserID) ?? ""
          }))
        )
    );
  }, []);
  return (
    <div className="row">
      <InsertFields {...props} />
      {signatures.length > 0 && (
        <Field title={TranslationService.translate.Signature}>
          <Dropdown
            onChange={
              props.propGen.num.notRequired("Mail_SignatureID").onChange
            }
            items={signatures}
            optionLabel={TranslationService.translate.None}
            value={props.data.Mail_SignatureID}
          />
        </Field>
      )}
    </div>
  );
};
